import { Text, BlockStack, Spinner, InlineStack, EmptyState, Scrollable, InlineGrid, Link, useBreakpoints, Tooltip, Icon } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import EditOrderModal from 'entities/orders/edit_order.modal';
import helpers from 'helpers/index';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import __helpers from 'helpers/index';
import { useDeleteConfirm } from 'context/DeleteConfirmContext';
import HoverMarquee from 'components/HoverMarquee';
import 'media/css/draganddrop.scss';
import { useCurrentTicketMoving } from 'context/CurrentTicketMovingContext';
import { useNavigate } from 'react-router-dom';
import { useMutateTicketClone, useMutateTicketTemporaryDelete, useGetUnassignedTickets, useMutateTicketDetach } from 'queries/ticket.query';

import { callStatusCallbackForDisplay } from './components/ticket_helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDongSign, faCreditCard, faParachuteBox, faPercent, faTaxi, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import dayjs from '../../utils/dayjs';
import { ENUM_ORDER_INVOICE_TYPE, ENUM_ORDER_PAYMENT_STATUS, useMutateOrderRemove } from 'queries/orders.query';
import { ENUM_TRANSACTION_PAYMENT_METHOD } from 'queries/transactions.query';
import AccountingShell from './components/AccountingShell';
import { TypedTripManifest } from 'queries/trip_manifest.query';

import 'media/css/sidebar.scss';
import Capacity from 'components/Capacity';
import { useNotification } from 'context/NotificationContext';
import { getLatestOrderTransaction } from 'entities/orders/order.util';

// Props cho component
interface UnassignedTicketsColumnProps {
  /** Ngày được chọn để lọc vé */
  selectedHour: string;
  manifest: TypedTripManifest;
}

/**
 * Component hiển thị danh sách các vé chưa được xếp chuyến (status=0),
 * được nhóm theo giờ khởi hành.
 */
export function UnassignedTicketsColumn({ selectedHour, manifest }: UnassignedTicketsColumnProps) {
  const { addNotification } = useNotification();

  const rootSearchPath = window.location.search;
  let StringQuery: Record<string, any> = useMemo(() => {
    return helpers.ExtractUrl(rootSearchPath);
  }, [rootSearchPath]);

  const navigate = useNavigate();

  // // Memo hóa params lấy danh sách vé chưa xếp chuyến
  const ticketQueryParams = useMemo(() => {
    if (!StringQuery.d) return;
    if (!StringQuery.journey_id) return;
    return {
      journey_id: StringQuery.journey_id,
      selectedHour: selectedHour,
      departure_date: StringQuery.d || '',
      limit: 400, // Lấy tối đa 400 vé, đủ cho 1 ngày
    };
  }, [StringQuery.d, StringQuery.journey_id, selectedHour]);

  const { data: unassignedTicketData, isLoading } = useGetUnassignedTickets(ticketQueryParams);

  const currentOrderForEditing = useRef(null);
  const [showEditTicketModal, setShowEditTicketModal] = useState(false);

  /** Vị trí ghế đã ngồi */
  /* Tự động highlight ghế nếu có hash trên URL */
  const highlight_for_a_ticket = useMemo(() => {
    return String(window.location.hash || ' ').replace('#', '');
  }, [window.location.hash]);

  const [currentSeatActive, setCurrentSeatActive] = useState<string[]>([highlight_for_a_ticket]);

  /** Chọn vé để di chuyển */
  /** Chọn vé để di chuyển */
  /** Chọn vé để di chuyển */
  /** Chọn vé để di chuyển */

  const { currentTicketMoving, setCurrentTicketMoving } = useCurrentTicketMoving();

  /** nhân đôi vé */
  const { mutateAsync: cloneATicket } = useMutateTicketClone();
  const handleCloneTicket = useCallback(async (__ticket_id) => {
    // clone ...
    try {
      let { ticket_id } = await cloneATicket(__ticket_id);
      setCurrentTicketMoving([ticket_id]);
    } catch (e) {}
  }, []);

  /** tách vé */
  const { mutateAsync: detachATicket } = useMutateTicketDetach();
  const handleDetactTicket = useCallback(async (ticket_id) => {
    try {
      await detachATicket(ticket_id);
      addNotification('info', 'Tách vé thành công!');
      setCurrentSeatActive([ticket_id]);
    } catch (e) {}
  }, []);

  /** Cancel ticket */
  const { showDeleteConfirm } = useDeleteConfirm();
  const { mutateAsync: cancelATicket } = useMutateTicketTemporaryDelete();

  const handleCancelTicket = useCallback(
    async (ticket_id: any) => {
      let confirmCancelTicket = await showDeleteConfirm(
        'Bạn có chắc muốn hủy vé này?',
        `Việc hủy vé có thể được thông báo cho khách hàng qua Zalo. Tài xế cũng có thể nhận được các thông báo tương tự.`
      );
      if (confirmCancelTicket) {
        try {
          await cancelATicket(ticket_id);
          setCurrentSeatActive(null);
        } catch (e) {}
      }
    },
    [StringQuery?.h]
  );

  const currentSeatActiveCallback = useCallback(
    (order_id: any) => {
      if (!unassignedTicketData) return;
      //!SECTION
      let allSameOrderCodeTicketID = unassignedTicketData?.map((al) => {
        if (al?.order_id === order_id) {
          return al.ticket_id;
        }
        return null;
      });
      return setCurrentSeatActive(allSameOrderCodeTicketID);
    },
    [unassignedTicketData]
  );
  const searchAllTicketIDInThatOrder = useCallback(
    (order_id: string) => {
      // tìm tất thảy các ID của cái đơn hàng này
      let fTicketIDSameOrderID = unassignedTicketData?.filter((el) => el.order_id === order_id);
      const allTicketIDsInThatOrder = fTicketIDSameOrderID.map((al) => al.ticket_id);
      setCurrentSeatActive(allTicketIDsInThatOrder);
      return allTicketIDsInThatOrder;
    },
    [currentSeatActive, unassignedTicketData]
  );

  /** Phát hiện click ra ngoài */
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        // 👉 Click ngoài
        setCurrentSeatActive([]);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const { mdDown } = useBreakpoints();

  /** xóa một đơn hàng */
  const { mutateAsync: removeOrder } = useMutateOrderRemove();
  const handleRemoveOrder = useCallback(
    async (order_id) => {
      let confirmCancelTicket = await showDeleteConfirm('Bạn có chắc muốn xóa bỏ đơn hàng này?', `Việc xóa đơn hàng sẽ không thể hoàn tác.`);
      if (confirmCancelTicket) {
        try {
          await removeOrder(order_id);
          setCurrentSeatActive([]);
        } catch (e) {}
      }
    },
    [removeOrder]
  );

  // 3. Render giao diện
  const renderContent = () => {
    if (isLoading) {
      return (
        <InlineStack align="center" blockAlign="center" gap="400">
          <Spinner size="small" />
          <Text as="p" tone="subdued">
            Đang tải danh sách vé...
          </Text>
        </InlineStack>
      );
    }

    return (
      <Scrollable horizontal={false} shadow={false} hint>
        {/* Danh sách các vé trong nhóm giờ đó */}
        <div ref={wrapperRef}>
          <BlockStack gap="300">
            {Array.isArray(unassignedTicketData)
              ? unassignedTicketData.map((item, index) => (
                  <Draggable draggableId={item.ticket_id} key={item.ticket_id} index={index} isDragDisabled={!!mdDown}>
                    {(provided, snapshot) => {
                      const order = item.orders;
                      const isSelected = currentSeatActive?.some((id) => id === item?.ticket_id);
                      const isMoving = helpers.isArray(currentTicketMoving) && currentTicketMoving?.some((id) => id === item?.ticket_id);
                      const hasBankTransaction = !!(
                        order?.transactions && order?.transactions.find((t) => t.payment_method === ENUM_TRANSACTION_PAYMENT_METHOD.BANK)
                      );
                      const hasDebitTransaction = !!(
                        order?.transactions &&
                        order?.transactions.find((t) =>
                          [
                            ENUM_TRANSACTION_PAYMENT_METHOD.DEBT_AGENT,
                            ENUM_TRANSACTION_PAYMENT_METHOD.DEBT_CUSTOMER,
                            ENUM_TRANSACTION_PAYMENT_METHOD.THIRD_PARTY,
                          ].includes(t.payment_method)
                        )
                      );
                      const paidFull = Number(order?.total_paid) >= Number(order?.total_must_pay);

                      return (
                        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                          <div
                            key={`d_` + item.ticket_id}
                            onClick={(e) => {
                              currentSeatActiveCallback(item?.order_id);
                            }}
                            style={{ cursor: 'pointer' }}
                            className={snapshot.isDragging ? 'prepair_dragable_item' : ''}
                          >
                            <div
                              id={`ticket_id:` + item.ticket_id}
                              className={`
                          ticket_element clickable 
                        ${order?.payment_status === 1 ? ' ticket_paid' : ''}
                        ${isSelected ? ' actived' : ''}
                        ${isMoving ? ' ticket_highlight' : ''} `}
                            >
                              <BlockStack gap="200">
                                <InlineStack align="space-between" blockAlign="center">
                                  <Tooltip content={`Ticket id ${item?.ticket_id} - Order Code ${item?.orders?.order_code}`}>
                                    <Text as="h3" variant="headingLg">
                                      {item?.orders.pickup_time}
                                    </Text>
                                  </Tooltip>
                                  <div className="ticket_badge_header_top">
                                    <InlineStack gap="200" align="end" blockAlign="center">
                                      {item?.orders?.order_invoice_info ? (
                                        <Tooltip content={item?.orders?.order_invoice_info?.tax_type}>
                                          <FontAwesomeIcon
                                            size={'lg'}
                                            icon={faPercent}
                                            color={
                                              item?.orders?.order_invoice_info?.tax_type === ENUM_ORDER_INVOICE_TYPE.COMPANY
                                                ? 'var(--p-color-icon-critical)'
                                                : 'var(--p-color-icon-emphasis)'
                                            }
                                          />
                                        </Tooltip>
                                      ) : (
                                        <Tooltip content="Thuế mặc định">
                                          <FontAwesomeIcon size={'lg'} icon={faPercent} color="var(--p-color-icon-secondary)" />
                                        </Tooltip>
                                      )}

                                      {callStatusCallbackForDisplay(
                                        item?.orders?.order_meta?.call_status,
                                        item?.orders?.order_meta?.call_last_updated_author_data?.display_name,
                                        item?.orders?.order_meta?.call_last_updated
                                      )}

                                      {item?.orders?.order_meta?.pickup_by_data ? (
                                        <Tooltip
                                          content={`Đón bởi ${item?.orders?.order_meta?.pickup_by_data?.display_name} lúc ${dayjs(Number(item?.orders?.order_meta?.pickup_at)).tz('Asia/Ho_Chi_Minh').format('HH:mm DD-MM-YYYY')}`}
                                        >
                                          <FontAwesomeIcon size={'lg'} icon={faTaxi} color="var(--p-color-icon-emphasis)" />
                                        </Tooltip>
                                      ) : (
                                        <Tooltip content="Đang chờ đón">
                                          <FontAwesomeIcon size={'lg'} icon={faTaxi} color="var(--p-color-icon-secondary)" />
                                        </Tooltip>
                                      )}
                                      {item?.orders?.order_meta?.dropoff_by_data ? (
                                        <Tooltip
                                          content={`Trả bởi ${item?.orders?.order_meta?.dropoff_by_data?.display_name} lúc ${dayjs(Number(item?.orders?.order_meta?.dropoff_at)).tz('Asia/Ho_Chi_Minh').format('HH:mm DD-MM-YYYY')}`}
                                        >
                                          <FontAwesomeIcon size={'lg'} icon={faParachuteBox} color="var(--p-color-icon-emphasis)" />
                                        </Tooltip>
                                      ) : (
                                        <Tooltip content="Chưa xuống xe">
                                          <FontAwesomeIcon size={'lg'} icon={faParachuteBox} color="var(--p-color-icon-secondary)" />
                                        </Tooltip>
                                      )}

                                      {order?.payment_status !== ENUM_ORDER_PAYMENT_STATUS.UNPAID ? (
                                        <Tooltip
                                          content={`Đã thanh toán ${helpers.formatNumberWithCommas(order?.total_paid)} / ${helpers.formatNumberWithCommas(order?.total_must_pay)}`}
                                        >
                                          {hasDebitTransaction ? (
                                            <FontAwesomeIcon size={'lg'} icon={faTriangleExclamation} color="var(--p-color-icon-critical)" />
                                          ) : hasBankTransaction ? (
                                            <FontAwesomeIcon
                                              size={'lg'}
                                              icon={faCreditCard}
                                              color={paidFull ? 'var(--p-color-icon-emphasis)' : 'var(--p-color-icon-critical)'}
                                            />
                                          ) : (
                                            <FontAwesomeIcon
                                              size={'lg'}
                                              icon={faDongSign}
                                              color={paidFull ? 'var(--p-color-icon-emphasis)' : 'var(--p-color-icon-critical)'}
                                            />
                                          )}
                                        </Tooltip>
                                      ) : (
                                        <Tooltip content="Chưa thanh toán">
                                          <FontAwesomeIcon size={'lg'} icon={faDongSign} color="var(--p-color-icon-secondary)" />
                                        </Tooltip>
                                      )}
                                    </InlineStack>
                                  </div>
                                </InlineStack>

                                <BlockStack gap="050" inlineAlign="end">
                                  <div className="ticket_address">
                                    <HoverMarquee speed={80}>{item?.orders?.pickup_address}</HoverMarquee>
                                  </div>
                                  {item?.orders?.pickup_note ? (
                                    <Tooltip content={item?.orders?.pickup_note}>
                                      <div className="ticket_address_note">{helpers.trimContentString(item?.orders?.pickup_note, 40)}</div>
                                    </Tooltip>
                                  ) : null}

                                  <div className="ticket_address">
                                    <HoverMarquee speed={80}>{item?.orders?.dropoff_address}</HoverMarquee>
                                  </div>
                                  {item?.orders?.dropoff_note ? (
                                    <Tooltip content={item?.orders?.dropoff_note}>
                                      <div className="ticket_address_note">{helpers.trimContentString(item?.orders?.dropoff_note, 40)}</div>
                                    </Tooltip>
                                  ) : null}
                                </BlockStack>

                                <br />
                                <InlineStack align="space-between" blockAlign="start" gap="100">
                                  <BlockStack gap="050">
                                    {item?.orders?.pickup_contact_phonenumber ? (
                                      <Text as="p" fontWeight="bold">
                                        {item?.orders?.pickup_contact_name}
                                      </Text>
                                    ) : null}

                                    <Text as="p" fontWeight="bold">
                                      <Link
                                        monochrome
                                        removeUnderline
                                        onClick={() => navigate('/customer/view/' + item?.orders?.customer_data?.user_id)}
                                      >
                                        {item?.orders?.customer_data?.display_name} <br /> {item?.orders?.customer_data?.user_phonenumber}
                                      </Link>
                                    </Text>
                                  </BlockStack>

                                  <div>
                                    <Text as="p" fontWeight="bold" alignment="end">
                                      {__helpers.formatNumberWithCommas(item?.price - item?.discount)}
                                    </Text>
                                    <Text as="p" alignment="end">
                                      Đơn: {__helpers.formatNumberWithCommas(item?.orders?.total_must_pay)} / Phí:{' '}
                                      {__helpers.formatNumberWithCommas(item?.orders?.total_fee)}
                                    </Text>
                                  </div>
                                </InlineStack>

                                <Text as="p" fontWeight="bold">
                                  Ghi chú: {item?.orders?.note || '--'}
                                </Text>

                                <div className="toolchain">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      currentOrderForEditing.current = item?.orders?.id;
                                      setShowEditTicketModal(true);
                                    }}
                                  >
                                    U
                                  </button>
                                  <Capacity
                                    current_user_can={'manage_driver_settlement'}
                                    extra_condition={!hasBankTransaction && !hasDebitTransaction && !order?.order_invoice_info && !order?.invoices}
                                  >
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveOrder(order.id);
                                      }}
                                      title="Xoá đơn hàng"
                                    >
                                      R
                                    </button>
                                  </Capacity>

                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      let allTicketSelected = searchAllTicketIDInThatOrder(item?.order_id);
                                      setCurrentTicketMoving(allTicketSelected);
                                    }}
                                  >
                                    M
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCancelTicket(item?.ticket_id);
                                    }}
                                  >
                                    D
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCloneTicket(item?.ticket_id);
                                    }}
                                  >
                                    C
                                  </button>

                                  {item?.orders?.total_seat > 1 && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDetactTicket(item?.ticket_id);
                                      }}
                                      title="Tách vé"
                                    >
                                      S
                                    </button>
                                  )}
                                </div>

                                <div className="ticket_author">{item?.orders?.author_data?.display_name}</div>
                              </BlockStack>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  </Draggable>
                ))
              : null}
            <div className="placeholder_drop_here">
              <Text as="p">Kéo thả vé vào đây</Text>
              <Text as="p">Để tách vé ra khỏi chuyến</Text>
            </div>
          </BlockStack>
        </div>
      </Scrollable>
    );
  };
  return (
    <div className="unsigned-ticket-sidebar">
      <BlockStack gap="400">
        {/** Chỉ cho người có quyền nhìn thấy */}
        <Capacity current_user_can={['view_invoice', 'manage_invoice']} extra_condition={!!manifest}>
          {manifest ? <AccountingShell tripManifestData={manifest} /> : 'LOADING'}
        </Capacity>

        <Text as="h3" variant="headingSm" tone="subdued">
          VÉ CHƯA VÀO CHUYẾN
        </Text>

        <Droppable droppableId="unsigned_ticket" type="SUB_ITEM" isDropDisabled={!!mdDown}>
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps} className="unsigned-ticket-droppable">
              {selectedHour ? (
                renderContent()
              ) : (
                <EmptyState heading=" " image="">
                  <p>Chọn giờ trước, và các vé chưa vào chuyến sẽ có tại đây.</p>
                </EmptyState>
              )}

              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </BlockStack>

      {showEditTicketModal && <EditOrderModal show order_id={currentOrderForEditing.current} onClose={() => setShowEditTicketModal(false)} />}
    </div>
  );
}

